import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { MxTabBar } from "@/components/shared/MxTabBar";
import { Thumbnail } from "@/components/shared/Thumbnail";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Search, AlertTriangle, Download, ShoppingCart, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useParts } from "@/hooks/useParts";
import { useAssets } from "@/hooks/useAssets";
import { useAssetPartIds } from "@/hooks/usePartAssets";
import { CreatePartDialog } from "@/components/parts/CreatePartDialog";
import { PartDetailSheet } from "@/components/parts/PartDetailSheet";
import { OrderPartDialog } from "@/components/parts/OrderPartDialog";
import { PartsExportDialog } from "@/components/shared/ExportDialog";
import type { Part } from "@/hooks/useParts";

const PAGE_SIZE = 50;

export default function Parts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("q") ?? "");
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [selectedPart, setSelectedPart] = useState<Part | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [orderPart, setOrderPart] = useState<Part | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [assetFilter, setAssetFilter] = useState<string>("");
  const [assetSearch, setAssetSearch] = useState("");
  const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);
  const [page, setPage] = useState(0);

  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
    if (debounceTimer) clearTimeout(debounceTimer);
    setDebounceTimer(setTimeout(() => {
      setDebouncedSearch(value);
      setSearchParams(value ? { q: value } : {}, { replace: true });
    }, 300));
  };

  const { data: allParts = [], isLoading } = useParts({
    search: debouncedSearch || undefined,
    low_stock_only: lowStockOnly || undefined,
  });
  const { data: assets = [] } = useAssets();
  const { data: assetPartIds } = useAssetPartIds(assetFilter || undefined);

  const selectedAsset = assets.find((a) => a.id === assetFilter);
  const filteredAssets = assets.filter((a) =>
    a.name.toLowerCase().includes(assetSearch.toLowerCase())
  );

  const parts = assetFilter && assetPartIds
    ? allParts.filter((p) => assetPartIds.has(p.id))
    : allParts;

  const lowStockCount = parts.filter((p) => p.is_low_stock).length;

  const totalPages = Math.ceil(parts.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(totalPages - 1, 0));
  const paginatedParts = parts.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const goToPage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="flex flex-col min-h-screen">
      <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Parts Inventory</h1>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setExportOpen(true)}>
          <Download className="h-4 w-4" />
          Export
        </Button>
        <CreatePartDialog />
      </header>
      <MxTabBar />
      <PartsExportDialog open={exportOpen} onOpenChange={setExportOpen} />

      <div className="flex-1 space-y-4">
        <div className="sticky top-[106px] z-10 flex items-center gap-3 bg-background border-b px-6 py-3 flex-wrap">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search parts..." className="pl-8" value={searchTerm} onChange={(e) => handleSearchChange(e.target.value)} />
          </div>

          {/* Asset filter */}
          <div className="relative">
            {selectedAsset ? (
              <div className="flex items-center gap-1.5 h-9 px-3 rounded-md border bg-primary/5 border-primary/30 text-sm">
                <span className="text-primary font-medium">{selectedAsset.name}</span>
                <button
                  onClick={() => { setAssetFilter(""); setAssetSearch(""); setPage(0); }}
                  className="text-primary/60 hover:text-primary ml-1"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <Input
                placeholder="Filter by asset…"
                value={assetSearch}
                onChange={(e) => { setAssetSearch(e.target.value); setAssetDropdownOpen(true); }}
                onFocus={() => setAssetDropdownOpen(true)}
                onBlur={() => setTimeout(() => setAssetDropdownOpen(false), 150)}
                className="w-48 h-9 text-sm"
              />
            )}
            {assetDropdownOpen && !selectedAsset && (
              <div className="absolute z-50 top-full mt-1 left-0 w-64 bg-popover border rounded-md shadow-md max-h-52 overflow-y-auto">
                {filteredAssets.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-2">No assets found</p>
                ) : (
                  filteredAssets.map((a) => (
                    <button
                      key={a.id}
                      onMouseDown={() => { setAssetFilter(a.id); setAssetSearch(""); setAssetDropdownOpen(false); setPage(0); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                    >
                      <span className="font-medium">{a.name}</span>
                      {a.asset_type && (
                        <span className="text-xs text-muted-foreground ml-2 capitalize">{a.asset_type.replace("_", " ")}</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <Button
            variant={lowStockOnly ? "default" : "outline"}
            size="sm"
            onClick={() => { setLowStockOnly(!lowStockOnly); setPage(0); }}
            className="gap-2"
          >
            <AlertTriangle className="h-4 w-4" />
            Low Stock{lowStockCount > 0 ? ` (${lowStockCount})` : ""}
          </Button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : parts.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground mb-4">
                {debouncedSearch || lowStockOnly || assetFilter ? "No parts match your filters" : "No parts in inventory yet"}
              </p>
              {!debouncedSearch && !lowStockOnly && <CreatePartDialog />}
            </Card>
          ) : (
            <>
              <div className="border rounded-lg bg-card">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b">
                      <TableHead className="bg-muted w-10 p-0"></TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground w-[35%]">Name</TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground hidden md:table-cell w-[20%]">OEM Part #</TableHead>
                      <TableHead className="bg-muted text-right font-semibold text-foreground w-[10%]">Stock</TableHead>
                      <TableHead className="bg-muted text-right font-semibold text-foreground hidden sm:table-cell">Min</TableHead>
                      <TableHead className="bg-muted text-right font-semibold text-foreground hidden lg:table-cell">Cost</TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground">Status</TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground text-center w-16">Purchase Order</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedParts.map((part) => (
                      <TableRow
                        key={part.id}
                        className="cursor-pointer hover:bg-muted/50 h-10 group"
                        onClick={() => { setSelectedPart(part); setSheetOpen(true); }}
                      >
                        <TableCell className="p-1 pl-2">
                          <Thumbnail url={part.image_url} size={72} className="h-7 w-7 rounded object-cover border border-border" />
                        </TableCell>
                        <TableCell className="font-medium py-2">{part.name}</TableCell>
                        <TableCell className="text-muted-foreground font-mono text-xs py-2 hidden md:table-cell">{part.part_number || "—"}</TableCell>
                        <TableCell className="text-right py-2 tabular-nums">
                          {part.qty_in_stock} <span className="text-muted-foreground text-xs">{part.unit}</span>
                        </TableCell>
                        <TableCell className="text-right py-2 text-muted-foreground tabular-nums hidden sm:table-cell">{part.min_quantity}</TableCell>
                        <TableCell className="text-right py-2 text-muted-foreground hidden lg:table-cell">
                          {part.unit_cost != null ? `$${part.unit_cost.toFixed(2)}` : "—"}
                        </TableCell>
                        <TableCell className="py-2">
                          {part.is_low_stock ? (
                            <Badge variant="destructive" className="text-[10px] gap-1 px-1.5 py-0">
                              <AlertTriangle className="h-2.5 w-2.5" />Low
                            </Badge>
                          ) : (
                            <Badge className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0">OK</Badge>
                          )}
                        </TableCell>
                        <TableCell className="py-2 pr-2 text-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); setOrderPart(part); }}
                            className="p-1 rounded hover:bg-primary/10 hover:text-primary text-muted-foreground transition-colors"
                            title="Add to Purchase Order"
                          >
                            <ShoppingCart className="h-3.5 w-3.5" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, parts.length)} of {parts.length} part{parts.length !== 1 ? "s" : ""}
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="h-7 w-7" disabled={safePage === 0} onClick={() => goToPage(safePage - 1)}>
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs text-muted-foreground px-2">
                      Page {safePage + 1} of {totalPages}
                    </span>
                    <Button variant="outline" size="icon" className="h-7 w-7" disabled={safePage >= totalPages - 1} onClick={() => goToPage(safePage + 1)}>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <PartDetailSheet
        part={selectedPart}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onPartUpdated={setSelectedPart}
      />

      {orderPart && (
        <OrderPartDialog
          part={orderPart}
          open={!!orderPart}
          onOpenChange={(v) => { if (!v) setOrderPart(null); }}
        />
      )}
    </div>
  );
}
